import { createContext, useContext, useEffect, useMemo, useState, ReactNode, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { createLoginRequest } from "./msalConfig";
import type { TenantType } from "./azureAdConfig";
import { usePostHog } from "@posthog/react";

export interface AuthContextType {
    token: string | null;
    setToken: (token: string | null) => void;
    logout: () => void;
    login: () => Promise<void>;
    isAuthenticated: boolean;
    isLoading: boolean;
    selectedTenant: TenantType | null;
    authMethod: "azure" | "token" | null;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}

const TENANT_STORAGE_KEY = "hockeyly_selected_tenant";

const AuthProvider = ({ children }: AuthProviderProps) => {
    const { instance, accounts, inProgress } = useMsal();
    const [token, setToken] = useState<string | null>(null);
    const posthog = usePostHog();
    const [isLoading, setIsLoading] = useState(true);
    const [selectedTenant, setSelectedTenant] = useState<TenantType | null>(() => {
        const stored = localStorage.getItem(TENANT_STORAGE_KEY);
        return stored as TenantType | null;
    });

    useEffect(() => {
        const getToken = async () => {
            if (inProgress === InteractionStatus.None && accounts.length > 0) {
                const tenant = selectedTenant || (localStorage.getItem(TENANT_STORAGE_KEY) as TenantType);
                const tokenRequest = tenant ? createLoginRequest(tenant) : { scopes: ["User.Read"] };

                try {
                    const response = await instance.acquireTokenSilent({
                        ...tokenRequest,
                        account: accounts[0],
                    });
                    setToken(response.accessToken);
                    posthog.identify(accounts[0].username, {
                        name: accounts[0].name ?? accounts[0].username,
                        email: accounts[0].username,
                    });
                    // eslint-disable-next-line no-console
                    console.log("Token: ", response.accessToken);
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.error("Failed to acquire token silently:", error);
                    // Clear storage and redirect to login if token acquisition fails
                    localStorage.removeItem(TENANT_STORAGE_KEY);
                    setToken(null);
                }
            } else if (inProgress === InteractionStatus.None && accounts.length === 0) {
                setToken(null);
            }

            if (inProgress === InteractionStatus.None) {
                setIsLoading(false);
            }
        };

        getToken();
    }, [instance, accounts, inProgress, selectedTenant, posthog]);

    const login = useCallback(async () => {
        await instance.loginRedirect({ scopes: ["User.Read"] });
    }, [instance]);

    const logout = useCallback(async () => {
        posthog.reset();
        setToken(null);
        localStorage.removeItem(TENANT_STORAGE_KEY);
        setSelectedTenant(null);

        const accounts = instance.getAllAccounts();
        for (const account of accounts) {
            await instance.clearCache({ account });
        }

        instance.logoutRedirect();
    }, [instance, posthog]);

    const contextValue = useMemo(
        () => ({
            token,
            setToken,
            logout,
            login,
            isAuthenticated: !!token && accounts.length > 0,
            isLoading,
            selectedTenant,
            authMethod: "azure" as const,
        }),
        [token, accounts.length, isLoading, login, logout, selectedTenant]
    );

    return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};

export default AuthProvider;
